import { useMemo } from "react";

interface BenchmarkWODTrackerProps {
  data: Record<string, unknown>;
}

interface WODEntry {
  name: string;
  time: number;
  trend: string;
  is_pr: boolean;
}

function formatWodTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function BenchmarkWODTracker({ data }: BenchmarkWODTrackerProps) {
  const wods = useMemo(() => {
    const raw = data.benchmark_wods as Record<string, { latest_value?: number; trend?: string; is_pr?: boolean }> | undefined;
    if (!raw) return [];
    return Object.entries(raw).map(
      ([name, entry]): WODEntry => ({
        name,
        time: entry.latest_value ?? 0,
        trend: entry.trend ?? "stable",
        is_pr: entry.is_pr ?? false,
      }),
    );
  }, [data]);

  const engineCapacity = typeof data.engine_capacity_pct === "number" ? data.engine_capacity_pct : null;
  const trainingAge = data.training_age as string | undefined;

  if (!wods.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Benchmark WODs</h3>
        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
          No benchmark WOD data yet. Record benchmark WOD times to track progress.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Benchmark WODs</h3>
        <div className="flex items-center gap-3">
          {trainingAge && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {trainingAge}
            </span>
          )}
          {engineCapacity !== null && (
            <span className="text-[10px] font-mono text-emerald-400">
              Engine: {(engineCapacity as number).toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1">
        {/* Header */}
        <div className="grid grid-cols-[1fr_80px_40px_40px] gap-2 text-[10px] text-muted-foreground uppercase tracking-wider px-2 pb-1 border-b border-white/[0.06]">
          <span>WOD</span>
          <span className="text-right">Time</span>
          <span className="text-center">Trend</span>
          <span className="text-center">PR</span>
        </div>

        {wods.map((wod) => (
          <div
            key={wod.name}
            className="grid grid-cols-[1fr_80px_40px_40px] gap-2 items-center px-2 py-1.5 rounded hover:bg-white/[0.03] transition-colors"
          >
            <span className="text-xs font-medium text-foreground truncate">{wod.name}</span>
            <span className="text-xs font-mono tabular-nums text-foreground text-right">
              {wod.time > 0 ? formatWodTime(wod.time) : "—"}
            </span>
            <span className="text-center">
              {wod.trend === "improving" ? (
                <span className="text-emerald-400 text-sm">↑</span>
              ) : wod.trend === "declining" ? (
                <span className="text-red-400 text-sm">↓</span>
              ) : (
                <span className="text-muted-foreground text-sm">→</span>
              )}
            </span>
            <span className="text-center">
              {wod.is_pr && <span className="text-amber-400 text-[10px] font-bold">PR</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
